/**
 * Bot AI for SURGE. Three difficulty levels with increasing sophistication.
 * All logic runs client-side — no APIs, no cost.
 */

import { scoreHand } from './scoring';

const getDifficulty = (state) => state?.botDifficulty || 'medium';

function getRankCounts(hand) {
  const counts = new Map();
  for (const c of hand) {
    if (c.isJoker) continue;
    counts.set(c.rank, (counts.get(c.rank) || 0) + 1);
  }
  return counts;
}

function scoreWithout(hand, card) {
  const without = hand.filter((c) => c.id !== card.id);
  return scoreHand(without).points;
}

function getDiscardedRanks(state) {
  const counts = new Map();
  for (const entry of state.discardHistory || []) {
    if (entry.rank) counts.set(entry.rank, (counts.get(entry.rank) || 0) + 1);
  }
  return counts;
}

function getOpponentPickedRanks(state) {
  const pickedRanks = new Set();
  const playerIndex = state.currentPlayerIndex;
  for (let i = 0; i < (state.opponentPickups || []).length; i++) {
    if (i === playerIndex) continue;
    for (const pickup of state.opponentPickups[i]) {
      if (pickup.rank) pickedRanks.add(pickup.rank);
    }
  }
  return pickedRanks;
}

function suitFlushPotential(hand, card) {
  if (card.isJoker) return 0;
  const suitCount = hand.filter((c) => !c.isJoker && c.suit === card.suit).length;
  if (suitCount >= 5) return 3;
  if (suitCount === 4) return 1.5;
  if (suitCount === 3) return 0.5;
  return 0;
}

/**
 * Easy bot: follows basic logic (keep pairs, discard high-rank singles)
 * but makes occasional mistakes (~20% of the time picks randomly).
 */
function chooseDiscardEasy(hand) {
  if (!hand || hand.length === 0) return null;
  if (hand.length === 1) return hand[0];

  if (Math.random() < 0.2) {
    return hand[Math.floor(Math.random() * hand.length)];
  }

  const rankCounts = getRankCounts(hand);
  const singles = hand.filter((c) => !c.isJoker && rankCounts.get(c.rank) === 1);

  if (singles.length > 0) {
    singles.sort((a, b) => {
      const aVal = a.rank === 1 ? 14 : a.rank;
      const bVal = b.rank === 1 ? 14 : b.rank;
      return bVal - aVal;
    });
    const topN = singles.slice(0, Math.max(1, Math.ceil(singles.length * 0.4)));
    return topN[Math.floor(Math.random() * topN.length)];
  }
  return hand[Math.floor(Math.random() * hand.length)];
}

/**
 * Medium bot: evaluates each discard by scoring the remaining hand,
 * but adds random noise so it doesn't play perfectly every turn.
 * Also considers discard history to prefer "safe" discards.
 */
function chooseDiscardMedium(hand, state) {
  if (!hand || hand.length === 0) return null;
  if (hand.length === 1) return hand[0];

  const discardedRanks = getDiscardedRanks(state);

  let bestCard = hand[0];
  let bestScore = -Infinity;

  for (const card of hand) {
    let score = scoreWithout(hand, card);

    if (!card.isJoker && discardedRanks.has(card.rank)) {
      score += 0.3;
    }

    score += (Math.random() - 0.5) * 1.2;

    if (score > bestScore) {
      bestScore = score;
      bestCard = card;
    }
  }
  return bestCard;
}

/**
 * Hard bot: maximises remaining-hand score, penalises discarding ranks
 * opponents want, prefers safe (already-discarded) ranks, and accounts
 * for flush potential when deciding what to keep.
 */
function chooseDiscardHard(hand, state) {
  if (!hand || hand.length === 0) return null;
  if (hand.length === 1) return hand[0];

  const pickedRanks = getOpponentPickedRanks(state);
  const discardedRanks = getDiscardedRanks(state);

  let bestCard = hand[0];
  let bestScore = -Infinity;

  for (const card of hand) {
    let score = scoreWithout(hand, card);

    if (!card.isJoker && pickedRanks.has(card.rank)) {
      score -= 2;
    }

    if (!card.isJoker && discardedRanks.has(card.rank)) {
      score += 0.5;
    }

    score -= suitFlushPotential(hand, card) * 0.4;

    if (score > bestScore) {
      bestScore = score;
      bestCard = card;
    }
  }
  return bestCard;
}

/**
 * Evaluate whether picking up the discard pile card improves the hand.
 * Returns the card to discard after pickup, or null if not worth it.
 */
function evaluatePickup(hand, discardPile, difficulty, state) {
  if (!discardPile || !hand || hand.length === 0) return null;

  const currentScore = scoreHand(hand).points;
  const handWithPickup = [...hand, discardPile];
  const canDiscard = handWithPickup.filter((c) => c.id !== discardPile.id);

  const pickedRanks = difficulty === 'hard' ? getOpponentPickedRanks(state) : new Set();
  const discardedRanks =
    difficulty !== 'easy' ? getDiscardedRanks(state) : new Map();

  let bestCard = null;
  let bestScore = -Infinity;

  for (const card of canDiscard) {
    const without = handWithPickup.filter((c) => c.id !== card.id);
    let score = scoreHand(without).points;

    if (difficulty === 'hard') {
      if (!card.isJoker && pickedRanks.has(card.rank)) score -= 2;
      if (!card.isJoker && discardedRanks.has(card.rank)) score += 0.5;
      score -= suitFlushPotential(without, card) * 0.4;
    }

    if (difficulty === 'medium') {
      if (!card.isJoker && discardedRanks.has(card.rank)) score += 0.3;
    }

    if (score > bestScore) {
      bestScore = score;
      bestCard = card;
    }
  }

  const threshold = difficulty === 'hard' ? 0 : difficulty === 'medium' ? 1 : 2;
  if (bestScore > currentScore + threshold) {
    return bestCard;
  }
  return null;
}

/**
 * Decide whether to call SURGE based on difficulty, hand strength,
 * and game score context.
 */
function shouldSurge(hand, difficulty, state) {
  const turnCount = state.turnCount || 0;
  const points = scoreHand(hand).points;
  const gameScores = state.gameScores || [];
  const myIndex = state.currentPlayerIndex;
  const myTotal = gameScores[myIndex] || 0;
  const bestOpponent = Math.max(
    ...gameScores.filter((_, i) => i !== myIndex),
    0
  );
  const scoreLead = myTotal - bestOpponent;

  if (difficulty === 'easy') {
    if (turnCount < 6) return false;
    if (points >= 6) return Math.random() < 0.15;
    return turnCount >= 10 && Math.random() < 0.08;
  }

  if (difficulty === 'medium') {
    if (turnCount < 4) return false;
    const urgency = scoreLead < -8 ? 1.6 : scoreLead < 0 ? 1.2 : 1;
    if (points >= 8) return Math.random() < 0.5 * urgency;
    if (points >= 6 && turnCount >= 7) return Math.random() < 0.3 * urgency;
    if (points >= 4 && turnCount >= 12) return Math.random() < 0.15 * urgency;
    return false;
  }

  // Hard
  if (turnCount < 3) return false;

  const urgency = scoreLead < -10 ? 1.8 : scoreLead < -5 ? 1.3 : scoreLead > 10 ? 0.7 : 1;

  if (points >= 10) return Math.random() < Math.min(1, 0.85 * urgency);
  if (points >= 8 && turnCount >= 5) return Math.random() < 0.6 * urgency;
  if (points >= 6 && turnCount >= 8) return Math.random() < 0.4 * urgency;

  const lastPickups = (state.opponentPickups || []).reduce((sum, arr) => sum + arr.length, 0);
  if (points >= 5 && lastPickups >= 3 && turnCount >= 6) return Math.random() < 0.3 * urgency;

  if (scoreLead < -15 && points >= 3 && turnCount >= 10) return Math.random() < 0.25;

  return false;
}

/**
 * Compute a randomised "thinking" delay in ms for a bot turn.
 * Harder bots respond faster on average; all have variance so it
 * doesn't feel mechanical.
 */
export function getBotDelay(difficulty) {
  const base = difficulty === 'easy' ? 1400 : difficulty === 'hard' ? 600 : 900;
  const variance = difficulty === 'easy' ? 800 : difficulty === 'hard' ? 500 : 600;
  return Math.round(base + Math.random() * variance);
}

/**
 * Main entry: returns { action, card? } for the bot's move.
 */
export function getBotMove(state) {
  if (state.phase !== 'playing') return null;
  const playerIndex = state.currentPlayerIndex;
  if (playerIndex === 0) return null;

  const hand = state.hands[playerIndex] || [];
  const discardPile = state.discardPile;
  const difficulty = getDifficulty(state);

  if (shouldSurge(hand, difficulty, state)) {
    return { action: 'serg' };
  }

  if (discardPile) {
    if (difficulty === 'easy') {
      if (Math.random() < 0.35) {
        const afterPickup = evaluatePickup(hand, discardPile, 'easy', state);
        if (afterPickup) return { action: 'pickUpAndDiscard', card: afterPickup };
      }
    } else {
      const afterPickup = evaluatePickup(hand, discardPile, difficulty, state);
      if (afterPickup) return { action: 'pickUpAndDiscard', card: afterPickup };
    }
  }

  let cardToDiscard;
  if (difficulty === 'easy') {
    cardToDiscard = chooseDiscardEasy(hand);
  } else if (difficulty === 'hard') {
    cardToDiscard = chooseDiscardHard(hand, state);
  } else {
    cardToDiscard = chooseDiscardMedium(hand, state);
  }

  if (cardToDiscard) return { action: 'discard', card: cardToDiscard };
  return { action: 'serg' };
}
